/**
 * Brand Category Tabs Component
 * Handles brand/product carousel with tab navigation
 * Features:
 * - Tab-based navigation between brands
 * - Previous/Next arrow navigation
 * - Active state management
 * - History-based tab persistence (selected tab survives refresh & back navigation)
 */
import { setupAdjacentSlidePreload } from "@/utils/adjacent-slide-preload.util";
export function BrandCategoryTabs(element) {
    const totalBrands = parseInt(element.dataset.totalBrands || "2", 10);
    let currentSlide = 0;
    // Get all interactive elements
    const brandTabs = element.querySelectorAll("[data-brand-tab]");
    const brandSlides = element.querySelectorAll("[data-brand-slide]");
    const prevBtn = element.querySelector("[data-brand-tab-prev]");
    const nextBtn = element.querySelector("[data-brand-tab-next]");
    if (!brandTabs.length || !brandSlides.length) {
        console.warn("[BrandCategoryTabs] Missing required elements. Component disabled.");
        return () => { };
    }
    const adjacentSlidePreload = setupAdjacentSlidePreload({
        element,
        slides: brandSlides,
        totalItems: totalBrands,
        getCurrentIndex: () => currentSlide,
    });
    /**
     * Get the tab id (slug) for a given index
     */
    const getTabId = (index) => {
        const tab = brandTabs[index];
        return tab ? tab.getAttribute("data-tab-id") : null;
    };
    /**
     * Find the tab index by its tab id (slug)
     */
    const getIndexByTabId = (tabId) => {
        for (let i = 0; i < brandTabs.length; i++) {
            if (brandTabs[i].getAttribute("data-tab-id") === tabId) {
                return i;
            }
        }
        return -1;
    };
    /**
     * Read the selected tab from URL hash.
     * Hash format: #brand-tab=<tab-id>
     */
    const getTabFromHash = () => {
        const hash = window.location.hash;
        if (!hash)
            return -1;
        const params = new URLSearchParams(hash.substring(1));
        const tabId = params.get("brand-tab");
        if (!tabId)
            return -1;
        return getIndexByTabId(tabId);
    };
    /**
     * Update the URL hash with the selected tab
     */
    const updateHash = (index, pushState) => {
        const tabId = getTabId(index);
        if (!tabId)
            return;
        const params = new URLSearchParams(window.location.hash.substring(1));
        params.set("brand-tab", tabId);
        const newHash = "#" + params.toString();
        if (pushState) {
            history.pushState(null, "", newHash);
        }
        else {
            history.replaceState(null, "", newHash);
        }
    };
    /**
     * Navigate to a specific slide index
     */
    const goToSlide = (index, updateHistory = true) => {
        // Set all tabs to inactive
        brandTabs.forEach((tab) => {
            tab.setAttribute("data-active", "false");
            tab.setAttribute("aria-selected", "false");
        });
        // Hide all slides
        brandSlides.forEach((slide) => {
            slide.setAttribute("data-active", "false");
            slide.setAttribute("aria-hidden", "true");
        });
        // Set current tab to active
        const currentTab = brandTabs[index];
        const currentSlideElement = brandSlides[index];
        if (currentTab) {
            currentTab.setAttribute("data-active", "true");
            currentTab.setAttribute("aria-selected", "true");
        }
        if (currentSlideElement) {
            currentSlideElement.setAttribute("data-active", "true");
            currentSlideElement.setAttribute("aria-hidden", "false");
        }
        // Update current slide index
        currentSlide = index;
        adjacentSlidePreload.onSlideChanged(index);
        // Update URL hash
        if (updateHistory) {
            updateHash(index, true);
        }
    };
    /**
     * Navigate to next slide
     */
    const nextSlide = () => {
        const nextIndex = (currentSlide + 1) % totalBrands;
        goToSlide(nextIndex);
    };
    /**
     * Navigate to previous slide
     */
    const prevSlide = () => {
        const prevIndex = (currentSlide - 1 + totalBrands) % totalBrands;
        goToSlide(prevIndex);
    };
    /**
     * Handle browser back/forward navigation
     */
    const onPopState = () => {
        const index = getTabFromHash();
        if (index >= 0 && index !== currentSlide) {
            goToSlide(index, false);
        }
    };
    // Store event handlers for cleanup
    const tabHandlers = new Map();
    const navigationHandlers = [];
    // Add event listeners to brand tabs
    brandTabs.forEach((tab, index) => {
        const handler = () => goToSlide(index);
        tabHandlers.set(tab, handler);
        tab.addEventListener("click", handler);
    });
    // Add event listeners to navigation buttons
    if (prevBtn) {
        const handler = () => prevSlide();
        navigationHandlers.push({ element: prevBtn, handler });
        prevBtn.addEventListener("click", handler);
    }
    if (nextBtn) {
        const handler = () => nextSlide();
        navigationHandlers.push({ element: nextBtn, handler });
        nextBtn.addEventListener("click", handler);
    }
    // Listen for browser back/forward
    window.addEventListener("popstate", onPopState);
    // Restore tab from URL hash, or default to first slide
    const restoredIndex = getTabFromHash();
    if (restoredIndex >= 0) {
        goToSlide(restoredIndex, false);
    }
    else {
        goToSlide(0, false);
    }
    // Cleanup function
    return () => {
        // Remove tab event listeners
        brandTabs.forEach((tab) => {
            const handler = tabHandlers.get(tab);
            if (handler) {
                tab.removeEventListener("click", handler);
            }
        });
        tabHandlers.clear();
        // Remove navigation button event listeners
        navigationHandlers.forEach(({ element, handler }) => {
            element.removeEventListener("click", handler);
        });
        // Remove popstate listener
        window.removeEventListener("popstate", onPopState);
        adjacentSlidePreload.destroy();
    };
}
